/**
 * Update-listing builder — the boundary between the edit-listing dialog
 * (`components/my-listings/edit-listing-dialog.tsx`) and the HTTP route
 * `PATCH /api/listings/[id]`.
 *
 *   components/my-listings/edit-listing-dialog.tsx        ┐
 *                                                         ├── intent ──▶ updateListing
 *                                                         │                   │
 *                                                         │                   ├── safeParse (boundary)
 *                                                         │                   └── PATCH ─── 2xx ──▶ UpdateListingResult
 *                                                         │                              │
 *                                                         │                              └── !ok ──▶ throws UpdateListingError(status, serverError)
 *
 * Why this exists:
 *  - L1 schema (`PatchListingRequestSchema`) shipped in commit 33bfe808
 *    (Block A). The route still reads `await request.json().catch(() => null)`
 *    (`app/api/listings/[id]/route.ts:69`); the two inline producers in
 *    `edit-listing-dialog.tsx:{442,596}` hand-assemble bodies and call
 *    `fetch` directly. Block B closes the L2 gap (G23 in
 *    `FRONTEND_WIRING_STRATEGY §3.2`) by introducing one canonical builder
 *    so a Walking Skeleton test can drive producer + route in one call —
 *    no hand-coded body, no parallel `satisfies PatchListingRequest`.
 *  - Pattern mirrors the create sibling (`lib/listings/listing-create.ts`)
 *    and the chat cluster (`lib/chat/chat-send.ts`) — same `seal` ▶ `postJson`
 *    seam, same `Built<T>` brand, same contract-error/server-error split.
 *
 * Design notes:
 *  - Intent IS the wire body: PATCH semantics make all fields optional, the
 *    contract union (`PatchListingRequest`) already models that, so there is
 *    no spread-omit-not-null assembly to do (callers either include a field
 *    or omit the key entirely — `JSON.stringify` drops `undefined`).
 *  - Response is NOT pinned by Zod (same reasoning as `chat-send.ts` /
 *    `offer-builders.ts`). Route returns `{ success: true, listing: <row> }`;
 *    the `UpdateListingResult.listing` is typed loosely as
 *    `Record<string, unknown>` so callers cast to their domain `Listing`
 *    shape (the producer already does this — see `edit-listing-dialog.tsx`).
 *  - On `!res.ok` throws `UpdateListingError(message, status, serverError)`.
 *    The route emits both the legacy `{ error }` field AND a stable machine
 *    `code` on validation/preorder-locked/auction-locked rejections; the
 *    caller maps to user-facing toast/dialog state.
 *
 * @see app/api/listings/[id]/route.ts — PATCH handler (L1.5 boundary parse
 *      lands in commit B3)
 * @see components/my-listings/edit-listing-dialog.tsx — sole producer today
 *      (closed by Block C of the edit-listing hardening workstream)
 * @see packages/contract/src/listing-patch.ts — PatchListingRequestSchema
 * @see lib/listings/listing-create.ts — sibling builder (CREATE)
 * @see lib/chat/chat-send.ts — pattern reference (Sibling-E §4 #3c)
 * @see .tondocs/skills/hex.md §2 — Functional Core / Imperative Shell
 * @see .tondocs/todos/listings/p1-edit-listing-l1-l2-l4-hardening.md
 */

import {
  PatchListingRequestSchema,
  seal,
  type PatchListingRequest,
} from "@kraahl/contract"
import { postJson } from "@/lib/api/post-json"

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * Thrown when the assembled body fails the contract Zod parse before fetch.
 * Programmer-bug signal: caller passed an intent the contract refuses. The
 * server would 400 anyway; this surfaces it at the responsible line.
 * Mirror of `ChatSendContractError`.
 */
export class UpdateListingContractError extends Error {
  readonly issues: ReadonlyArray<{ path: string; message: string }>
  constructor(issues: ReadonlyArray<{ path: string; message: string }>) {
    super(
      `[listings PATCH] contract parse failed: ${issues
        .map((i) => `${i.path}: ${i.message}`)
        .join("; ")}`,
    )
    this.name = "UpdateListingContractError"
    this.issues = issues
  }
}

/**
 * Thrown when `PATCH /api/listings/[id]` returns a non-2xx response. The
 * delivery layer reads `status` (403 AUCTION_LOCKED / 409 PREORDER_LOCKED
 * / 400 VALIDATION_ERROR) and `serverError` (the raw `error` string from
 * the payload) to drive its toast / dialog state. Mirror of `ChatSendError`.
 */
export class UpdateListingError extends Error {
  readonly status: number
  readonly serverError: string | null
  constructor(message: string, status: number, serverError: string | null) {
    super(message)
    this.name = "UpdateListingError"
    this.status = status
    this.serverError = serverError
  }
}

// ---------------------------------------------------------------------------
// Result type
// ---------------------------------------------------------------------------

/**
 * Mirror of the route's success envelope (`{ success: true, listing: <row> }`).
 * The row is `listings` table shape — kept loose here (same reasoning as
 * `SendChatMessageResult`): tightening would force this file to import the
 * domain Listing type, and the caller already casts to its own model.
 */
export interface UpdateListingResult {
  success: true
  listing: Record<string, unknown>
}

// ---------------------------------------------------------------------------
// Builder
// ---------------------------------------------------------------------------

/**
 * safeParse the intent, PATCH `/api/listings/[id]`, and return the updated
 * row on success. Throws `UpdateListingContractError` if the body fails
 * the contract before fetch, `UpdateListingError(message, status,
 * serverError)` on any non-2xx response.
 */
export async function updateListing(args: {
  listingId: string
  intent: PatchListingRequest
}): Promise<UpdateListingResult> {
  const { listingId, intent } = args

  const parsed = PatchListingRequestSchema.safeParse(intent)
  if (!parsed.success) {
    throw new UpdateListingContractError(
      parsed.error.issues.map((i) => ({
        path: i.path.join("."),
        message: i.message,
      })),
    )
  }

  // Sibling-E §4 #3c: `seal` after safeParse converts the body into a
  // `Built<PatchListingRequest>`. `postJson` accepts ONLY `Built<T>` — a
  // hand-rolled literal in a component cannot reach this seam.
  const res = await postJson(
    `/api/listings/${listingId}`,
    seal<PatchListingRequest>(intent),
    { method: "PATCH" },
  )

  if (!res.ok) {
    const errPayload = await res.json().catch(() => null)
    const serverError =
      errPayload && typeof (errPayload as { error?: unknown }).error === "string"
        ? (errPayload as { error: string }).error
        : null
    const message = serverError ?? `Update listing (${res.status})`
    throw new UpdateListingError(message, res.status, serverError)
  }

  return (await res.json()) as UpdateListingResult
}
